import { ethers } from 'ethers';
import DocumentManagerV2ABI from '../contracts/DocumentManagerV2.json';

// Contract configuration - loaded from environment variables
const CONTRACT_ADDRESS = process.env.REACT_APP_CONTRACT_ADDRESS || "0xb19f78B9c32dceaA01DE778Fa46784F5437DF373";
const SEPOLIA_RPC_URL = process.env.REACT_APP_RPC_URL || "https://sepolia.infura.io/v3/YOUR_INFURA_KEY";
const SEPOLIA_CHAIN_ID = parseInt(process.env.REACT_APP_CHAIN_ID || "11155111", 10);

// Global variables
let provider = null;
let signer = null;
let contract = null;

class BlockchainServiceV2 {
  constructor() {
    this.isInitialized = false;
    this.currentWallet = null;
  }

  // Initialize the blockchain connection
  async initialize() {
    try {
      console.log('🚀 Initializing DocumentManagerV2 blockchain service...');

      if (typeof window.ethereum === 'undefined') {
        throw new Error('MetaMask is not installed. Please install MetaMask browser extension.');
      }

      // Request account access
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      console.log('👤 Connected accounts:', accounts.length);
      
      // Create provider and signer
      provider = new ethers.BrowserProvider(window.ethereum);
      signer = await provider.getSigner();
      this.currentWallet = await signer.getAddress();
      console.log('📝 Current wallet:', this.currentWallet);

      // Create contract instance
      contract = new ethers.Contract(CONTRACT_ADDRESS, DocumentManagerV2ABI, signer);
      console.log('📄 Contract instance created');

      // Check network
      const network = await provider.getNetwork();
      console.log('🌐 Network:', network.name, 'Chain ID:', network.chainId.toString());
      
      if (network.chainId !== BigInt(SEPOLIA_CHAIN_ID)) {
        console.log('⚠️ Not on Sepolia network, attempting to switch...');
        await this.switchToSepolia();
      }

      // Listen for account changes
      window.ethereum.on('accountsChanged', this.handleAccountsChanged.bind(this));
      window.ethereum.on('chainChanged', this.handleChainChanged.bind(this));

      this.isInitialized = true;
      console.log('✅ DocumentManagerV2 service initialized successfully');
      
      return {
        success: true,
        wallet: this.currentWallet,
        network: 'Sepolia'
      };
    } catch (error) {
      console.error('❌ Error initializing blockchain service:', error);
      throw error;
    }
  }

  // Handle account changes (wallet switching)
  handleAccountsChanged(accounts) {
    console.log('🔄 Wallet switched in blockchainServiceV2:', accounts[0]);
    
    if (accounts.length === 0) {
      // User disconnected wallet
      console.log('👋 Wallet disconnected');
      this.currentWallet = null;
      this.isInitialized = false;
    } else {
      // User switched to a different account
      this.currentWallet = accounts[0];
      
      // Reinitialize signer and contract with new account
      if (provider) {
        provider.getSigner().then(newSigner => {
          signer = newSigner;
          contract = new ethers.Contract(CONTRACT_ADDRESS, DocumentManagerV2ABI, signer);
          console.log('✅ Contract reinitialized with new wallet:', this.currentWallet);
        }).catch(error => {
          console.error('❌ Error reinitializing signer:', error);
        });
      }
    }
    
    // Note: Page reload removed - let WalletContext handle UI updates
    // If you need to reload data, dispatch a custom event instead
    window.dispatchEvent(new CustomEvent('walletChanged', { 
      detail: { address: accounts[0] } 
    }));
  }

  // Handle chain changes
  handleChainChanged(chainId) {
    console.log('🔄 Network changed:', chainId);
    
    // Check if still on Sepolia
    const newChainId = parseInt(chainId, 16);
    if (newChainId !== SEPOLIA_CHAIN_ID) {
      console.warn('⚠️ Not on Sepolia network anymore. ChainId:', newChainId);
    }
    
    // Dispatch event for components to handle
    window.dispatchEvent(new CustomEvent('networkChanged', { 
      detail: { chainId: newChainId } 
    }));
    
    // Reload page to reset state (optional - you can handle this in components instead)
    window.location.reload();
  }

  // Switch to Sepolia network
  async switchToSepolia() {
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0xaa36a7' }], // Sepolia chain ID in hex
      });
      console.log('✅ Switched to Sepolia network');
    } catch (switchError) {
      if (switchError.code === 4902) {
        // Network not added to MetaMask
        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [{
            chainId: '0xaa36a7',
            chainName: 'Sepolia Testnet',
            nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
            rpcUrls: [SEPOLIA_RPC_URL],
            blockExplorerUrls: ['https://sepolia.etherscan.io']
          }]
        });
      } else {
        throw switchError;
      }
    }
  }

  // Get current connected wallet
  async getCurrentWallet() {
    if (!this.isInitialized) {
      await this.initialize();
    }
    return this.currentWallet;
  }

  // Switch wallet (prompt MetaMask to connect different wallet)
  async switchWallet() {
    try {
      console.log('🔄 Requesting wallet switch...');
      const accounts = await window.ethereum.request({
        method: 'wallet_requestPermissions',
        params: [{ eth_accounts: {} }]
      }).then(() => window.ethereum.request({ method: 'eth_requestAccounts' }));
      
      this.currentWallet = accounts[0];
      console.log('✅ Switched to wallet:', this.currentWallet);
      
      // Reinitialize with new wallet
      signer = await provider.getSigner();
      contract = new ethers.Contract(CONTRACT_ADDRESS, DocumentManagerV2ABI, signer);
      
      return this.currentWallet;
    } catch (error) {
      console.error('❌ Error switching wallet:', error);
      throw error;
    }
  }

  // Upload document to blockchain
  async uploadDocument(ipfsHash, fileName, fileSize, documentType = 'document') {
    try {
      if (!this.isInitialized) await this.initialize();

      console.log('📤 Uploading document to blockchain...');
      console.log('📝 File:', fileName);
      console.log('📦 IPFS Hash:', ipfsHash);
      console.log('🔗 Wallet:', this.currentWallet);

      const tx = await contract.uploadDocument(
        ipfsHash,
        fileName,
        fileSize,
        documentType
      );

      console.log('⏳ Transaction sent:', tx.hash);
      const receipt = await tx.wait();
      console.log('✅ Transaction confirmed:', receipt.hash);
      console.log('⛽ Gas used:', receipt.gasUsed?.toString());

      // Get document ID from event
      const event = receipt.logs.find(log => {
        try {
          const parsed = contract.interface.parseLog(log);
          return parsed.name === 'DocumentUploaded';
        } catch (e) {
          return false;
        }
      });

      const documentId = event ? contract.interface.parseLog(event).args.documentId : null;

      // Extract gas information
      const gasUsed = receipt.gasUsed ? receipt.gasUsed.toString() : null;
      const gasPrice = receipt.gasPrice ? receipt.gasPrice.toString() : (tx.gasPrice ? tx.gasPrice.toString() : null);

      return {
        success: true,
        transactionHash: receipt.hash,
        blockNumber: receipt.blockNumber,
        documentId: documentId,
        uploadedByWallet: this.currentWallet,
        gasUsed: gasUsed,
        gasPrice: gasPrice
      };
    } catch (error) {
      console.error('❌ Error uploading document:', error);
      throw error;
    }
  }

  // Share document with another wallet
  async shareDocument(documentId, recipientWallet, permission = 'read') {
    try {
      if (!this.isInitialized) await this.initialize();

      console.log('🤝 Sharing document...');
      console.log('📄 Document ID:', documentId);
      console.log('👤 Recipient:', recipientWallet);
      console.log('🔐 Permission:', permission);
      console.log('🔗 Sharing from wallet:', this.currentWallet);

      // Pre-check: Verify document ownership before attempting transaction
      try {
        const doc = await contract.getDocument(documentId);
        const documentOwner = doc.owner.toLowerCase();
        const currentWalletLower = this.currentWallet.toLowerCase();
        
        console.log('📋 Document owner:', documentOwner);
        console.log('👤 Current wallet:', currentWalletLower);
        
        if (documentOwner !== currentWalletLower) {
          const shortOwner = `${documentOwner.substring(0, 6)}...${documentOwner.substring(documentOwner.length - 4)}`;
          const shortCurrent = `${currentWalletLower.substring(0, 6)}...${currentWalletLower.substring(currentWalletLower.length - 4)}`;
          return {
            success: false,
            error: `Only the document owner can share this document.\n\nDocument owner: ${shortOwner}\nYour wallet: ${shortCurrent}\n\nPlease connect with the wallet that originally uploaded this document.`
          };
        }
      } catch (ownerCheckError) {
        console.warn('⚠️ Could not verify ownership, proceeding with transaction:', ownerCheckError.message);
        // Continue with the transaction - let the blockchain handle ownership validation
      }

      // Validate permission
      if (permission !== 'read' && permission !== 'write') {
        throw new Error('Permission must be "read" or "write"');
      }

      const tx = await contract.shareDocument(
        documentId,
        recipientWallet,
        permission
      );

      console.log('⏳ Transaction sent:', tx.hash);
      const receipt = await tx.wait();
      console.log('✅ Transaction confirmed:', receipt.hash);
      console.log('⛽ Gas used:', receipt.gasUsed?.toString());

      // Extract gas information
      const gasUsed = receipt.gasUsed ? receipt.gasUsed.toString() : null;
      const gasPrice = receipt.gasPrice ? receipt.gasPrice.toString() : (tx.gasPrice ? tx.gasPrice.toString() : null);

      return {
        success: true,
        transactionHash: receipt.hash,
        blockNumber: receipt.blockNumber,
        sharedWithWallet: recipientWallet,
        gasUsed: gasUsed,
        gasPrice: gasPrice
      };
    } catch (error) {
      console.error('❌ Error sharing document:', error);
      
      // Parse blockchain error messages for user-friendly display
      const errorMsg = this.parseBlockchainError(error, 'share');
      return {
        success: false,
        error: errorMsg
      };
    }
  }

  // Parse blockchain errors into user-friendly messages
  parseBlockchainError(error, operation = 'operation') {
    const msg = error.message || error.toString();
    
    // Document existence errors
    if (msg.includes('Document does not exist')) {
      return 'This document is not registered on the blockchain. Please upload it first from File Manager.';
    }
    
    // Owner/permission errors
    if (msg.includes('Only owner can share')) {
      return 'You can only share documents you uploaded. Please connect with the wallet that originally uploaded this document.';
    }
    if (msg.includes('Only owner can revoke')) {
      return 'Only the document owner can revoke share permissions.';
    }
    if (msg.includes('No permission to update') || msg.includes('Unauthorized')) {
      return 'You don\'t have permission to update this document. You need to be the owner or have write access.';
    }
    
    // Transaction errors
    if (msg.includes('user rejected') || msg.includes('User denied') || msg.includes('ACTION_REJECTED')) {
      return 'Transaction was cancelled in MetaMask.';
    }
    if (msg.includes('insufficient funds')) {
      return 'Insufficient ETH in your wallet for gas fees. Please add some Sepolia ETH.';
    }
    if (msg.includes('nonce too low')) {
      return 'Transaction conflict detected. Please wait a moment and try again.';
    }
    if (msg.includes('replacement fee too low') || msg.includes('underpriced')) {
      return 'Transaction fee too low. Please try again with higher gas.';
    }
    
    // Network errors
    if (msg.includes('network') || msg.includes('disconnected') || msg.includes('timeout')) {
      return 'Network connection error. Please check your internet connection and try again.';
    }
    if (msg.includes('Invalid chain') || msg.includes('wrong network')) {
      return 'Please switch to Sepolia testnet in MetaMask.';
    }
    
    // Contract-specific errors
    if (msg.includes('already shared')) {
      return 'This document is already shared with this user.';
    }
    if (msg.includes('Invalid address')) {
      return 'Invalid wallet address. Please verify the recipient\'s wallet address.';
    }
    if (msg.includes('Document is not active')) {
      return 'This document has been deactivated and cannot be shared.';
    }
    
    // Wallet connection errors
    if (msg.includes('MetaMask') || msg.includes('wallet')) {
      return 'Wallet connection issue. Please ensure MetaMask is connected and try again.';
    }
    
    // Default error
    return `Blockchain ${operation} failed: ${msg.substring(0, 100)}`;
  }

  // Update document (owner or write permission holders)
  async updateDocument(documentId, newIpfsHash) {
    try {
      if (!this.isInitialized) await this.initialize();

      console.log('📝 Updating document...');
      console.log('📄 Document ID:', documentId);
      console.log('📦 New IPFS Hash:', newIpfsHash);
      console.log('🔗 Updating from wallet:', this.currentWallet);

      const tx = await contract.updateDocument(documentId, newIpfsHash);

      console.log('⏳ Transaction sent:', tx.hash);
      const receipt = await tx.wait();
      console.log('✅ Transaction confirmed:', receipt.hash);

      return {
        success: true,
        transactionHash: receipt.hash,
        blockNumber: receipt.blockNumber
      };
    } catch (error) {
      console.error('❌ Error updating document:', error);
      
      // Parse blockchain error messages for user-friendly display
      const errorMsg = this.parseBlockchainError(error, 'update');
      return {
        success: false,
        error: errorMsg
      };
    }
  }

  // Revoke share permission
  async revokeShare(documentId, revokeFromWallet) {
    try {
      if (!this.isInitialized) await this.initialize();

      console.log('🚫 Revoking share permission...');
      console.log('📄 Document ID:', documentId);
      console.log('👤 Revoking from:', revokeFromWallet);

      const tx = await contract.revokeShare(documentId, revokeFromWallet);

      console.log('⏳ Transaction sent:', tx.hash);
      const receipt = await tx.wait();
      console.log('✅ Share revoked:', receipt.hash);

      return {
        success: true,
        transactionHash: receipt.hash,
        blockNumber: receipt.blockNumber
      };
    } catch (error) {
      console.error('❌ Error revoking share:', error);
      throw error;
    }
  }

  // Check if user has write permission
  async hasWritePermission(documentId, walletAddress = null) {
    try {
      if (!this.isInitialized) await this.initialize();

      const wallet = walletAddress || this.currentWallet;
      const hasPermission = await contract.hasWritePermission(documentId, wallet);

      console.log(`🔐 Write permission for ${wallet}:`, hasPermission);
      return hasPermission;
    } catch (error) {
      console.error('❌ Error checking write permission:', error);
      return false;
    }
  }

  // Check if user has read permission
  async hasReadPermission(documentId, walletAddress = null) {
    try {
      if (!this.isInitialized) await this.initialize();

      const wallet = walletAddress || this.currentWallet;
      const hasPermission = await contract.hasReadPermission(documentId, wallet);

      console.log(`🔐 Read permission for ${wallet}:`, hasPermission);
      return hasPermission;
    } catch (error) {
      console.error('❌ Error checking read permission:', error);
      return false;
    }
  }

  // Check if document exists on blockchain using the contract's documentExists mapping
  async documentExists(documentId) {
    try {
      if (!this.isInitialized) await this.initialize();
      
      console.log('🔍 Checking if document exists on blockchain:', documentId);
      
      // Directly call the documentExists mapping in the smart contract
      // This returns a boolean directly without reverting if document doesn't exist
      const exists = await contract.documentExists(documentId);
      
      console.log('📄 Document exists on blockchain:', exists);
      return exists;
    } catch (error) {
      console.error('❌ Error checking document existence:', error.message);
      return false;
    }
  }

  // Get document details
  async getDocument(documentId) {
    try {
      if (!this.isInitialized) await this.initialize();

      const doc = await contract.getDocument(documentId);
      
      return {
        ipfsHash: doc.ipfsHash,
        owner: doc.owner,
        timestamp: Number(doc.timestamp),
        fileName: doc.fileName,
        fileSize: Number(doc.fileSize),
        isActive: doc.isActive,
        documentType: doc.documentType,
        version: Number(doc.version)
      };
    } catch (error) {
      console.error('❌ Error getting document:', error);
      throw error;
    }
  }

  // Get specific version of document
  async getDocumentVersion(documentId, version) {
    try {
      if (!this.isInitialized) await this.initialize();

      const ipfsHash = await contract.getDocumentVersion(documentId, version);
      return ipfsHash;
    } catch (error) {
      console.error('❌ Error getting document version:', error);
      throw error;
    }
  }

  // Get active shares for a document
  async getActiveDocumentShares(documentId) {
    try {
      if (!this.isInitialized) await this.initialize();

      const shares = await contract.getActiveDocumentShares(documentId);
      
      return shares.map(share => ({
        sharedWith: share.sharedWith,
        permission: share.permission,
        timestamp: Number(share.timestamp),
        isActive: share.isActive
      }));
    } catch (error) {
      console.error('❌ Error getting document shares:', error);
      return [];
    }
  }

  // Get all documents owned by a wallet
  async getOwnerDocuments(walletAddress = null) {
    try {
      if (!this.isInitialized) await this.initialize();

      const wallet = walletAddress || this.currentWallet;
      const documentIds = await contract.getOwnerDocuments(wallet);

      console.log(`📂 Found ${documentIds.length} documents for ${wallet}`);
      return documentIds;
    } catch (error) {
      console.error('❌ Error getting owner documents:', error);
      return [];
    }
  }

  // Verify document authenticity
  async verifyDocument(documentId, ipfsHash) {
    try {
      if (!this.isInitialized) await this.initialize();

      const isValid = await contract.verifyDocument(documentId, ipfsHash);
      return isValid;
    } catch (error) {
      console.error('❌ Error verifying document:', error);
      return false;
    }
  }

  // Deactivate document
  async deactivateDocument(documentId) {
    try {
      if (!this.isInitialized) await this.initialize();

      const tx = await contract.deactivateDocument(documentId);
      const receipt = await tx.wait();

      return {
        success: true,
        transactionHash: receipt.hash,
        blockNumber: receipt.blockNumber
      };
    } catch (error) {
      console.error('❌ Error deactivating document:', error);
      throw error;
    }
  }

  // Disconnect wallet
  disconnect() {
    this.isInitialized = false;
    this.currentWallet = null;
    provider = null;
    signer = null;
    contract = null;
    console.log('🔌 Disconnected from blockchain');
  }

  // Check if service is ready
  isReady() {
    return this.isInitialized && contract !== null;
  }

  // Get document version history (V2 specific)
  async getDocumentVersionHistory(documentId) {
    try {
      if (!this.isInitialized) {
        await this.initialize();
      }

      console.log('📜 Fetching version history for documentId:', documentId);

      // Get document info
      const doc = await contract.getDocument(documentId);
      const currentVersion = Number(doc.version);
      
      console.log('📊 Current version:', currentVersion);

      const versions = [];
      
      // Fetch all versions from newest to oldest
      for (let i = currentVersion; i >= 1; i--) {
        try {
          const versionData = await contract.getDocumentVersion(documentId, i);
          
          versions.push({
            versionNumber: i,
            ipfsHash: versionData,
            updatedBy: doc.owner, // V2 doesn't track per-version updater
            date: new Date().toISOString(), // V2 doesn't track per-version timestamp
            fileName: doc.fileName,
            fileSize: Number(doc.fileSize),
            changeLog: i === currentVersion ? 'Current version' : `Version ${i}`
          });
        } catch (error) {
          console.error(`Error fetching version ${i}:`, error);
        }
      }

      console.log(`✅ Fetched ${versions.length} versions`);
      
      return {
        success: true,
        versions
      };
    } catch (error) {
      console.error('❌ Error fetching version history:', error);
      return {
        success: false,
        error: error.message,
        versions: []
      };
    }
  }
}

// Export singleton instance
const blockchainServiceV2 = new BlockchainServiceV2();
export default blockchainServiceV2;
